import React, {Fragment} from 'react'
import {ProfileButton} from '../../features/recumbent/ProfileButton'
import {SigninButton} from 'common/SigninButton'
import {NotificationsPopover} from './NotificationsPopover'
import {PopoverLinkMenu, PopoverLinkMenuItem} from 'common/styleguide/PopoverLinkMenu'
import styles from './GlobalNav.scss'
import {CTA} from 'common/styleguide/CTA'
import cx from 'classnames'
import {browserHistory} from 'react-router'
import {CoreLink} from 'common/CoreLink'
import {Icon} from 'common/Icon'
import {Icon as LegacyIcon} from 'common/LegacyIcon'
import {getCurrentUser, getSurrogateLogin, experimentEnabled, currentUserHasPrivilege} from 'lib/utils'
import {photoProfile} from 'lib/urlHelpers'
import {base} from 'lib/styleguide/base.scss'
import widgets from 'lib/styleguide/widgets.scss'
import {GlobalSearch} from 'common/GlobalSearch'
import {CoreContext} from 'bundles/core/CoreContext'
import {AdminMenu} from './AdminMenu'
import {FormattedMessage as T, injectIntl} from 'react-intl'
import {featureNames, Msg} from 'lib/localization'
import {showModal} from 'lib/modalUtils'
import {CHANGE_LOCALE_MODAL} from 'common/modals/ChangeLocaleModal'
import {LocaleProvider} from 'bundles/core/LocaleProvider'

const currentUserFromConfig = getCurrentUser()
const surrogateLoginFromConfig = getSurrogateLogin()

const labelsForLocale = {
  'en-US': 'English',
  'es': 'Español',
  'fr': 'Français',
  'ja': '日本語',
  'en-UK': 'English',
  'de': 'Deutsche',
  'nl': 'Nederlands',
  'mz': 'Marklar'
}

const NavbarLink = (props) => (
  <CoreLink {...props} className={styles.NavbarLink} />
)

const NavbarMenuLink = ({expanded, ...props}) => (
  <CoreLink {...props} className={cx(styles.NavbarLink, expanded && styles.NavbarLinkExpanded)} />
)

// this is here because it needs to be defined before the logged in/out items
class StoriesMenu extends React.PureComponent {
  constructor (props) {
    super(props)
    this.state = { expanded: false }
  }

  componentDidMount () {
    this._removeHistoryListener = browserHistory.listen(this.closeMenu)
  }

  componentWillUnMount () {
    this._removeHistoryListener()
  }

  closeMenu = () => {
    this.setState({expanded: false})
  }

  toggleExpanded = () => {
    this.setState({expanded: !this.state.expanded})
  }

  render () {
    const {expanded} = this.state

    return <Fragment>
      <NavbarMenuLink onClick={this.toggleExpanded} expanded={expanded}>
        <span className={styles.navA} ref={r => { this._ref = r }}>
          <T id='GlobalNav.stories' defaultMessage='Stories' description='Label for a navigation menu that allows users to access the News, Journal, and Ride Reports features' />
          {expanded
            ? <LegacyIcon i='upChevron' size={20} className={styles.jumpTo} />
            : <LegacyIcon i='downChevron' size={20} className={styles.jumpTo} />}
        </span>
      </NavbarMenuLink>
      {expanded && <CoreContext.Consumer>
        {isCore => <PopoverLinkMenu
          target={this._ref}
          placement='bottom'
          onRequestClose={this.toggleExpanded}>
          <CoreContext.Provider value={isCore}>
            <LocaleProvider>
              <PopoverLinkMenuItem to='/ride_reports'><Msg m={featureNames.rideReports} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/journal'><Msg m={featureNames.journal} /></PopoverLinkMenuItem>
              {currentUserFromConfig.accountLevel === 11 && <PopoverLinkMenuItem to='/news'><T id='featureNames.newsPlusPress' defaultMessage='News + Press' /></PopoverLinkMenuItem>}
            </LocaleProvider>
          </CoreContext.Provider>
        </PopoverLinkMenu>}
      </CoreContext.Consumer>}
    </Fragment>
  }
}

// these are used while logged into organization accounts
const classicLoggedInItems = <Fragment>
  <NavbarLink href='/find'>Find</NavbarLink>
  <NavbarLink href='/routes/new'>Plan</NavbarLink>
  <NavbarLink href='/log'>Upload</NavbarLink>
  <NavbarLink href='/routes'>Routes</NavbarLink>
  <NavbarLink href='/activities'>Rides</NavbarLink>
  <NavbarLink to='/ride_reports'>Ride Reports</NavbarLink>
  <NavbarLink to='/goals'>Goals</NavbarLink>
  <NavbarLink href='/help'>Help</NavbarLink>
</Fragment>

const loggedInItems = <Fragment>
  <NavbarLink href='/find'><Msg m={featureNames.find} /></NavbarLink>
  <NavbarLink href='/routes/new'><Msg m={featureNames.routePlanner} /></NavbarLink>
  <StoriesMenu />
  <NavbarLink href='/help' id='global-nav-help'><Msg m={featureNames.help} /></NavbarLink>
</Fragment>

const loggedOutItems = <Fragment>
  <NavbarLink href='/features'><Msg m={featureNames.features} /></NavbarLink>
  <NavbarLink href='/upgrade'><Msg m={featureNames.upgrade} /></NavbarLink>
  <NavbarLink href='/organizations'><Msg m={featureNames.organizations} /></NavbarLink>
  <NavbarLink href='/find'><Msg m={featureNames.find} /></NavbarLink>
  <StoriesMenu />
</Fragment>

class _GlobalNav extends React.PureComponent {
  static defaultProps = {
    currentUser: currentUserFromConfig,
    surrogateLogin: surrogateLoginFromConfig,
    redesignActive: experimentEnabled('homepage_redesign')
  }

  constructor (props) {
    super(props)
    this.state = {
      mobileExpanded: false,
      notificationsExpanded: false,
      allowSearchExpand: true
    }
  }

  componentDidMount () {
    this._stopListeningToHistory = browserHistory.listen(this.onLocationChange)
    // min-width is from $navbar-breakpoint in variables.scss
    this._mobileMediaQuery = window.matchMedia('screen and (min-width: 960px)')
    this._mobileMediaQuery.addListener(this.onScreenSizeChange)

    // this is a lot of misery to go through for 191px, but
    // https://github.com/ridewithgps/ridewithgps/issues/7412
    this._goldilocksMediaQuery = window.matchMedia('screen and (min-width: 960px) and (max-width: 1151px)')
    this._goldilocksMediaQuery.addListener(this.onGoldilocksScreenSizeChange)
    if (this._goldilocksMediaQuery.matches) {
      this.onGoldilocksScreenSizeChange({matches: true})
    }
  }

  componentWillUnmount () {
    this._stopListeningToHistory()
    this._mobileMediaQuery.removeListener(this.onScreenSizeChange)
    this._goldilocksMediaQuery.removeListener(this.onGoldilocksScreenSizeChange)
  }

  onScreenSizeChange = (e) => {
    const {mobileExpanded} = this.state
    if (e.matches && mobileExpanded) {
      this.toggleMobileNav()
    }
  }

  onGoldilocksScreenSizeChange = (e) => {
    this.setState({allowSearchExpand: !e.matches})
  }

  // collapse mobile expand when history changes
  onLocationChange = () => {
    const {mobileExpanded} = this.state
    mobileExpanded && this.toggleMobileNav()
  }

  toggleMobileNav = () => {
    const {mobileExpanded} = this.state
    this.setState({mobileExpanded: !mobileExpanded, notificationsExpanded: false})
  }

  toggleNotifications = () => {
    const {notificationsExpanded} = this.state
    this.setState({notificationsExpanded: !notificationsExpanded})
  }

  componentDidUpdate () {
    const {mobileExpanded} = this.state
    if (mobileExpanded) {
      document.body.classList.add(styles.noScroll)
    } else {
      document.body.classList.remove(styles.noScroll)
    }
  }

  render () {
    const {currentUser, surrogateLogin, redesignActive, intl} = this.props
    const {mobileExpanded, notificationsExpanded, allowSearchExpand} = this.state

    const notifications = currentUser.notifications || currentUser.notifications_serialized
    const hasUnseenNotifications = currentUser.numUnreadNotifications > 0

    const navItems = currentUser.id
      ? redesignActive ? loggedInItems : classicLoggedInItems
      : loggedOutItems

    const freeUser = currentUser.id && currentUser.accountLevel <= 0
    const showNotifications = !!currentUser.id

    const className = cx(
      styles.GlobalNav,
      mobileExpanded && styles.mobileExpanded,
      surrogateLogin && styles.surrogateLogin
    )

    const currentLanguage = labelsForLocale[intl.locale]

    return (
      <nav className={className}>
        <div className={styles.navFull}>
          <div className={styles.group}>
            {currentUser.accountLevel === 3 // force <a> if user is club
              ? <a href='/' className={styles.brandingLink}>
                <Icon i='logo-rwgps-simplified' size={40} />
              </a>
              : <CoreLink to='/' className={styles.brandingLink}>
                <Icon i='logo-rwgps-simplified' size={40} />
              </CoreLink>
            }
            {redesignActive && currentUser.id && <JumpToMenu isAdmin={currentUser.accountLevel === 11} />}
            {navItems}
          </div>
          <div className={styles.group}>
            {experimentEnabled('localization') && !currentUser.id && <div className={styles.languagePickerButton} onClick={() => showModal(CHANGE_LOCALE_MODAL)}>
              {/* intl.locale gets coerced to 'en' in the case of our (invalid) marklar locale, so this breaks and is expected to look funny in Marklar mode. */}
              <Icon i='ios-globe-outline' size={24} /> {currentLanguage}
            </div>}
            {currentUser.id && <GlobalSearch currentUser={currentUser} allowSearchExpand={allowSearchExpand} />}
            {freeUser && <NavbarLink href='/choose_account'>Upgrade</NavbarLink>}
            {currentUser.accountLevel === 11 && <AdminMenu />}
            {showNotifications && currentUser.id && <a onClick={this.toggleNotifications} className={cx(styles.iconLink, styles.withMargin)} ref={r => { this._notificationIcon = r }}>
              <Icon i='md-notifications' size={24} className={cx(hasUnseenNotifications && styles.hasUnread)} />
            </a>}
            {!currentUser.id && <Fragment>
              <SigninButton id='navbar-signin' buttonText={<T id='GlobalNav.logIn' defaultMessage='Log In' />} noStyle className={styles.NavbarLink} />
              <CTA id='navbar-signup' inline className={styles.signUp} secondary href='/signup'><T id='GlobalNav.signUp' defaultMessage='Sign Up' /></CTA>
            </Fragment>}
            {currentUser.id && <ProfileButton id='profile-button' currentUser={currentUser} currentLanguage={currentLanguage} />}
          </div>
        </div>
        <div className={styles.navCollapsed}>
          <a onClick={this.toggleMobileNav} className={styles.iconLink}>
            <Icon i={mobileExpanded ? 'md-close' : 'md-menu'} size={24} />
          </a>
          <a href='/' className={styles.brandingLink}>
            <Icon i='logo-rwgps-simplified' size={40} />
          </a>
          {!currentUser.id && <a href='/signup' className={styles.signupCollapsed}>Sign Up</a>}
          {showNotifications && currentUser.id && <CoreLink to='/notifications' className={styles.iconLink}>
            <Icon i='md-notifications' size={24} className={cx(hasUnseenNotifications && styles.hasUnread)} />
          </CoreLink>}
        </div>
        {mobileExpanded && <GlobalNavMobile
          showUpgrade={freeUser}
          currentUser={currentUser}
          navItems={navItems}
          currentLanguage={currentLanguage} />}
        {notificationsExpanded && <NotificationsPopover
          notifications={notifications}
          target={this._notificationIcon}
          onRequestClose={this.toggleNotifications} />}
      </nav>
    )
  }
}

export const GlobalNav = injectIntl(_GlobalNav)

export class GlobalNavMobile extends React.PureComponent {
  render () {
    const {currentUser, navItems, showUpgrade, currentLanguage} = this.props
    return (
      <div className={cx(base, styles.GlobalNavMobile)}>
        {currentUser.id && <div className={styles.profileImg}>
          <CoreLink to='/'>
            <img src={photoProfile(currentUser.highlightedPhotoId)} />
          </CoreLink>
        </div>}
        <div className={styles.navMain}>
          {currentUser.id && <NavbarLink href='/'>Home</NavbarLink>}
          {navItems}
        </div>
        <div className={styles.logout}>
          {!currentUser.id && <SigninButton block id='navbar-signin' buttonText='Log In' noStyle className={cx(widgets.CTA, widgets.secondaryCTA)} />}
          {currentUser.id && <CTA margins={false} secondary href='/logout'>Log Out</CTA>}
          {showUpgrade && <NavbarLink href='/choose_account'>Upgrade</NavbarLink>}
          {experimentEnabled('localization') && <div className={cx(styles.languagePickerButton, styles.mobile)} onClick={() => showModal(CHANGE_LOCALE_MODAL)}>
            <Icon i='ios-globe-outline' size={24} /> {currentLanguage}
          </div>}
        </div>
      </div>
    )
  }
}

export class JumpToMenu extends React.Component {
  constructor (props) {
    super(props)
    this.state = { expanded: false }
  }

  componentDidMount () {
    this._removeHistoryListener = browserHistory.listen(this.closeMenu)
  }

  componentWillUnMount () {
    this._removeHistoryListener()
  }

  closeMenu = () => {
    this.setState({expanded: false})
  }

  toggleExpanded = () => {
    this.setState({expanded: !this.state.expanded})
  }

  // ProfileButton uses LegacyIcon, so we have to here for consistency
  render () {
    const {isAdmin} = this.props
    const {expanded} = this.state
    const canSeeRideCenter = currentUserHasPrivilege('activity_center')

    return <Fragment>
      <NavbarMenuLink onClick={this.toggleExpanded} expanded={expanded}>
        <span className={styles.navA} ref={r => { this._ref = r }}>
          <T id='GlobalNav.jumpTo' defaultMessage='Jump To' description='Label for a navigation menu that allows users to quickly access different areas of the site' />
          {expanded
            ? <LegacyIcon i='upChevron' size={20} className={styles.jumpTo} />
            : <LegacyIcon i='downChevron' size={20} className={styles.jumpTo} />}
        </span>
      </NavbarMenuLink>
      {expanded && <CoreContext.Consumer>
        {isCore => <PopoverLinkMenu
          target={this._ref}
          placement='bottom'
          onRequestClose={this.toggleExpanded}>
          <CoreContext.Provider value={isCore}>
            {/* We *really* need to fix the popoverlinkmenu-breaks-context issue! */}
            <LocaleProvider>
              <PopoverLinkMenuItem to='/dashboard'><Msg m={featureNames.dashboard} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/calendar'><Msg m={featureNames.calendar} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/routes'><Msg m={featureNames.routes} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/rides'><Msg m={featureNames.rides} /></PopoverLinkMenuItem>
              {isAdmin && <PopoverLinkMenuItem to='/collections'><Msg m={featureNames.collections} /></PopoverLinkMenuItem>}
              <PopoverLinkMenuItem
                href={canSeeRideCenter ? '/activities' : null}
                to={!canSeeRideCenter ? '/analyze' : null}>
                <Msg m={featureNames.analyze} />
              </PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/upload'><Msg m={featureNames.upload} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/friends'><Msg m={featureNames.friends} /></PopoverLinkMenuItem>
              <PopoverLinkMenuItem to='/more'><Msg m={featureNames.more} /></PopoverLinkMenuItem>
            </LocaleProvider>
          </CoreContext.Provider>
        </PopoverLinkMenu>}
      </CoreContext.Consumer>}
    </Fragment>
  }
}
